import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { cn } from '@/components/ui/cn';
import {
  areaPath,
  formatNumber,
  formatTime,
  linearScale,
  monotonePath,
  nearestIndex,
  niceTicks,
  type Point,
} from './scale';

/*
  LineChart — график «изменение во времени» дизайн-системы (DESIGN.md §5, методичка dataviz).

  Свойства семьи (одинаковы везде, не настраиваются потребителем):
  - плавная монотонная кривая 2 px, маркер ≥ 8 px с кольцом цвета поверхности;
  - под линией — градиент цвета серии от ~22 % у линии до 0 у основания (размывка, не блок);
  - сетка и оси — тонкие, сплошные, приглушённые (line / fg-subtle); текст — токены текста;
  - наведение: снап к ближайшей точке по X, лёгкий пунктир от точки к обеим осям, ореол вокруг
    маркера, тултип со значением крупнее подписи; то же — с клавиатуры (← →);
  - цвета — из токенов, поэтому тема (день/ночь, режим пульта) подхватывается сама.
  Одна серия — без легенды: её называет заголовок карточки.
*/

export interface Series {
  id: string;
  label: string;
  points: Point[]; // x — время в мс, y — значение
  /** Цвет серии: токен --chart-1…4; по умолчанию по порядку. */
  color?: string;
}

/** Событие на оси времени: деплой, миграция, перезагрузка, окно обслуживания, инцидент. */
export interface ChartEvent {
  x: number;
  kind: 'deploy' | 'migration' | 'reboot' | 'maintenance' | 'incident';
  label: string;
}

const EVENT_COLOR: Record<ChartEvent['kind'], string> = {
  deploy: 'var(--info-fg)',
  migration: 'var(--chart-3)',
  reboot: 'var(--warning-fg)',
  maintenance: 'var(--fg-subtle)',
  incident: 'var(--sev-4-line)',
};

const EVENT_TITLE: Record<ChartEvent['kind'], string> = {
  deploy: 'Выкладка',
  migration: 'Миграция',
  reboot: 'Перезагрузка',
  maintenance: 'Обслуживание',
  incident: 'Инцидент',
};

interface LineChartProps {
  series: Series[];
  height?: number;
  unit?: string;
  /** Нижняя граница оси Y (по умолчанию 0 — доли и проценты начинаются с нуля). */
  yMin?: number;
  yMax?: number;
  digits?: number;
  /** Горизонтальная линия порога (например, 80 %). */
  threshold?: { value: number; label?: string };
  /** Засечки событий на оси времени: видно, после чего началось (R29). */
  events?: ChartEvent[];
  /** Продолжение линии вперёд пунктиром: прогноз читается как «ещё не факт» (R30). */
  forecast?: { points: Point[]; label?: string };
  className?: string;
  ariaLabel?: string;
}

const SERIES_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)'];
const PAD = { top: 12, right: 12, bottom: 24, left: 40 };

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Запасное измерение: в скрытой вкладке ResizeObserver не вызывает колбэк, хотя разметка
    // уже посчитана, и график остаётся пустым до первого изменения размера. Таймер, в отличие
    // от requestAnimationFrame, в скрытой вкладке работает.
    const first = window.setTimeout(() => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setWidth(w);
    }, 0);
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => {
      window.clearTimeout(first);
      ro.disconnect();
    };
  }, []);
  return [ref, width] as const;
}

export function LineChart({
  series,
  height = 220,
  unit = '',
  yMin = 0,
  yMax,
  digits = 0,
  threshold,
  events,
  forecast,
  className,
  ariaLabel,
}: LineChartProps) {
  const id = useId();
  const [wrapRef, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const [eventHover, setEventHover] = useState<number | null>(null);

  const model = useMemo(() => {
    const all = series.flatMap((s) => s.points);
    if (all.length === 0 || width === 0) return null;
    const xs = series[0].points.map((p) => p.x);
    // Ось времени растягивается на прогноз: пунктир должен поместиться целиком.
    const fc = forecast?.points ?? [];
    const x0 = Math.min(...all.map((p) => p.x));
    const x1 = Math.max(...all.map((p) => p.x), ...fc.map((p) => p.x));
    // Явный yMax — точная граница (0–100 %); авто — с запасом 8 % над максимумом, порогом и прогнозом.
    const rawMax =
      yMax ?? Math.max(...all.map((p) => p.y), ...fc.map((p) => p.y), threshold?.value ?? 0) * 1.08;
    const nice = niceTicks(yMin, rawMax, 4);
    const max = yMax ?? nice.max;
    const min = nice.min;
    const ticks = nice.ticks.filter((t) => t <= max);
    const x = linearScale([x0, x1], [PAD.left, width - PAD.right]);
    const y = linearScale([min, max], [height - PAD.bottom, PAD.top]);
    const lines = series.map((s, i) => ({
      ...s,
      color: s.color ?? SERIES_COLORS[i % SERIES_COLORS.length],
      pts: s.points.map((p) => ({ x: x(p.x), y: y(p.y) })),
    }));
    // Подписи времени: 5 делений равномерно.
    const xTicks = Array.from({ length: 5 }, (_, i) => x0 + ((x1 - x0) * i) / 4);
    const forecastPts = fc.map((p) => ({ x: x(p.x), y: y(p.y) }));
    const marks = (events ?? []).filter((e) => e.x >= x0 && e.x <= x1).map((e) => ({ ...e, px: x(e.x) }));
    return { x, y, xs, ticks, xTicks, lines, baseline: y(min), forecastPts, marks };
  }, [series, width, height, yMin, yMax, threshold, events, forecast]);

  if (!model) return <div ref={wrapRef} style={{ height }} className={cn('w-full', className)} />;

  const { x, y, xs, ticks, xTicks, lines, baseline, forecastPts, marks } = model;
  const active = hover === null ? null : Math.min(hover, xs.length - 1);

  const onMove = (e: PointerEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left + PAD.left;
    setHover(nearestIndex(xs, x.invert(px)));
  };
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === 'ArrowRight') setHover((h) => Math.min((h ?? -1) + 1, xs.length - 1));
    if (e.key === 'ArrowLeft') setHover((h) => Math.max((h ?? xs.length) - 1, 0));
    if (e.key === 'Escape') setHover(null);
  };

  const activePoints =
    active === null ? [] : lines.map((l) => ({ line: l, p: l.pts[active], v: l.points[active] }));
  const tooltipX = active === null ? 0 : x(xs[active]);
  const tooltipRight = tooltipX > width * 0.65;

  return (
    <div ref={wrapRef} className={cn('relative w-full select-none', className)}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel ?? series.map((s) => s.label).join(', ')}
        tabIndex={0}
        onKeyDown={onKey}
        onBlur={() => setHover(null)}
        className="block overflow-visible outline-none"
      >
        <defs>
          {lines.map((l) => (
            <linearGradient key={l.id} id={`${id}-${l.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={l.color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={l.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>

        {/* Сетка и подписи Y — тонкие, сплошные, приглушённые */}
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={y(t)}
              y2={y(t)}
              stroke="var(--line)"
              strokeWidth={1}
            />
            <text
              x={PAD.left - 8}
              y={y(t)}
              dy="0.32em"
              textAnchor="end"
              className="fill-fg-subtle font-mono text-2xs tabular-nums"
            >
              {formatNumber(t, 0)}
            </text>
          </g>
        ))}
        {xTicks.map((t) => (
          <text
            key={t}
            x={x(t)}
            y={height - 6}
            textAnchor="middle"
            className="fill-fg-subtle font-mono text-2xs tabular-nums"
          >
            {formatTime.format(new Date(t))}
          </text>
        ))}

        {threshold && (
          <g>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={y(threshold.value)}
              y2={y(threshold.value)}
              stroke="var(--warning)"
              strokeWidth={1}
              strokeDasharray="2 4"
              opacity={0.8}
            />
            {threshold.label && (
              <text
                x={width - PAD.right}
                y={y(threshold.value) - 5}
                textAnchor="end"
                className="fill-fg-subtle text-2xs"
              >
                {threshold.label}
              </text>
            )}
          </g>
        )}

        {/* Серии: размывка + линия */}
        {lines.map((l) => (
          <g key={l.id}>
            <path d={areaPath(l.pts, baseline)} fill={`url(#${id}-${l.id})`} />
            <path
              d={monotonePath(l.pts)}
              fill="none"
              stroke={l.color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        ))}

        {/* Прогноз (R30): то же перо, но пунктиром и полупрозрачно — «ещё не факт», а не измерение */}
        {forecastPts.length > 0 && lines[0].pts.length > 0 && (
          <g className="pointer-events-none">
            <path
              d={monotonePath([lines[0].pts[lines[0].pts.length - 1], ...forecastPts])}
              fill="none"
              stroke={lines[0].color}
              strokeWidth={2}
              strokeDasharray="5 5"
              strokeLinecap="round"
              opacity={0.55}
            />
            <circle
              cx={forecastPts[forecastPts.length - 1].x}
              cy={forecastPts[forecastPts.length - 1].y}
              r={3.5}
              fill="var(--card)"
              stroke={lines[0].color}
              strokeWidth={2}
              opacity={0.8}
            />
            {forecast?.label && (
              <text
                x={forecastPts[forecastPts.length - 1].x}
                y={forecastPts[forecastPts.length - 1].y - 10}
                textAnchor="end"
                className="fill-fg-subtle text-2xs"
              >
                {forecast.label}
              </text>
            )}
          </g>
        )}

        {/* Наведение: пунктир к осям, ореол, маркер с кольцом */}
        {activePoints.map(({ line, p }) => (
          <g key={line.id} className="pointer-events-none">
            <line
              x1={p.x}
              x2={p.x}
              y1={p.y}
              y2={baseline}
              stroke="var(--fg-subtle)"
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.7}
            />
            <line
              x1={PAD.left}
              x2={p.x}
              y1={p.y}
              y2={p.y}
              stroke="var(--fg-subtle)"
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.7}
            />
            <circle cx={p.x} cy={p.y} r={10} fill={line.color} opacity={0.16} />
            <circle cx={p.x} cy={p.y} r={4.5} fill={line.color} stroke="var(--card)" strokeWidth={2} />
          </g>
        ))}

        {/* Область захвата указателя — шире графика, чтобы попадать не в линию, а в дату */}
        <rect
          x={PAD.left}
          y={PAD.top}
          width={Math.max(0, width - PAD.left - PAD.right)}
          height={Math.max(0, height - PAD.top - PAD.bottom)}
          fill="transparent"
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        />

        {/* Засечки событий (R29): видно, после чего началось */}
        {marks.map((m, i) => (
          <g
            key={`${m.x}-${m.kind}-${i}`}
            onPointerEnter={() => setEventHover(i)}
            onPointerLeave={() => setEventHover(null)}
          >
            <line
              x1={m.px}
              x2={m.px}
              y1={PAD.top}
              y2={height - PAD.bottom}
              stroke={EVENT_COLOR[m.kind]}
              strokeWidth={1}
              strokeDasharray="2 3"
              opacity={eventHover === i ? 0.9 : 0.45}
            />
            <rect
              x={m.px - 3.5}
              y={PAD.top - 7.5}
              width={7}
              height={7}
              transform={`rotate(45 ${m.px} ${PAD.top - 4})`}
              fill={EVENT_COLOR[m.kind]}
              opacity={eventHover === i ? 1 : 0.85}
            />
            <rect
              x={m.px - 9}
              y={0}
              width={18}
              height={height - PAD.bottom}
              fill="transparent"
              aria-label={`${EVENT_TITLE[m.kind]}: ${m.label}`}
            />
          </g>
        ))}
      </svg>

      {eventHover !== null && marks[eventHover] && (
        <div
          className="pointer-events-none absolute top-0 z-raised max-w-56 rounded-md border border-line bg-elevated px-3 py-1.5 shadow-lg"
          style={
            marks[eventHover].px > width * 0.65
              ? { right: width - marks[eventHover].px + 10 }
              : { left: marks[eventHover].px + 10 }
          }
        >
          <div className="text-2xs text-fg-subtle">
            {formatTime.format(new Date(marks[eventHover].x))} · {EVENT_TITLE[marks[eventHover].kind]}
          </div>
          <div className="text-xs font-medium text-fg">{marks[eventHover].label}</div>
        </div>
      )}

      {active !== null && (
        <div
          className="pointer-events-none absolute top-2 z-raised min-w-28 rounded-md border border-line bg-elevated px-3 py-2 shadow-lg"
          style={tooltipRight ? { right: width - tooltipX + 12 } : { left: tooltipX + 12 }}
        >
          <div className="text-2xs text-fg-subtle">{formatTime.format(new Date(xs[active]))}</div>
          {activePoints.map(({ line, v }) => (
            <div key={line.id} className="mt-1 flex items-baseline gap-2">
              <span
                className="inline-block h-0.5 w-3 rounded-full"
                style={{ background: line.color }}
                aria-hidden
              />
              <span className="font-mono text-sm font-semibold text-fg tabular-nums">
                {formatNumber(v.y, digits)}
                {unit && <span className="ml-0.5 text-xs font-normal text-fg-muted">{unit}</span>}
              </span>
              {lines.length > 1 && <span className="text-xs text-fg-muted">{line.label}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
